/**
 * GENERATED FILE — do not hand-edit. Run `node scripts/generate-countries.mjs`.
 *
 * Localized ISO 3166-1 country options for the checkout guest form and the
 * gift-voucher buyer form. Names are frozen at generation time (rather than
 * resolved at runtime via `Intl.DisplayNames`) so the server and the browser —
 * which run different ICU/CLDR versions — always render the identical option
 * text and order, i.e. no hydration mismatch.
 *
 * This is the guest's ADDRESS country (an accounting-record field, also pushed
 * to PYN as `guestCountryCode`). It is deliberately NOT the same list as
 * `PHONE_COUNTRIES` in `@/lib/phone`, which stays curated because each entry
 * needs a dial code + placeholder and must stay unambiguous for
 * `parseStoredPhone`.
 */

export interface CountryOption {
  code: string;
  name: string;
}

/**
 * Pinned to the top of the dropdown, in this order: Romania (the hotel's own
 * country, ~92% of bookings) and the source markets that actually appear in
 * `bookings.guest_country`, plus Austria as a neighbouring source market.
 */
export const FREQUENT_COUNTRY_CODES = ['RO', 'HU', 'DE', 'AT', 'GB', 'US', 'IT', 'NL'] as const;

const COUNTRIES_EN: CountryOption[] = [
  { code: 'AF', name: "Afghanistan" },
  { code: 'AX', name: "Åland Islands" },
  { code: 'AL', name: "Albania" },
  { code: 'DZ', name: "Algeria" },
  { code: 'AS', name: "American Samoa" },
  { code: 'AD', name: "Andorra" },
  { code: 'AO', name: "Angola" },
  { code: 'AI', name: "Anguilla" },
  { code: 'AG', name: "Antigua & Barbuda" },
  { code: 'AR', name: "Argentina" },
  { code: 'AM', name: "Armenia" },
  { code: 'AW', name: "Aruba" },
  { code: 'AU', name: "Australia" },
  { code: 'AT', name: "Austria" },
  { code: 'AZ', name: "Azerbaijan" },
  { code: 'BS', name: "Bahamas" },
  { code: 'BH', name: "Bahrain" },
  { code: 'BD', name: "Bangladesh" },
  { code: 'BB', name: "Barbados" },
  { code: 'BY', name: "Belarus" },
  { code: 'BE', name: "Belgium" },
  { code: 'BZ', name: "Belize" },
  { code: 'BJ', name: "Benin" },
  { code: 'BM', name: "Bermuda" },
  { code: 'BT', name: "Bhutan" },
  { code: 'BO', name: "Bolivia" },
  { code: 'BA', name: "Bosnia & Herzegovina" },
  { code: 'BW', name: "Botswana" },
  { code: 'BR', name: "Brazil" },
  { code: 'IO', name: "British Indian Ocean Territory" },
  { code: 'VG', name: "British Virgin Islands" },
  { code: 'BN', name: "Brunei" },
  { code: 'BG', name: "Bulgaria" },
  { code: 'BF', name: "Burkina Faso" },
  { code: 'BI', name: "Burundi" },
  { code: 'KH', name: "Cambodia" },
  { code: 'CM', name: "Cameroon" },
  { code: 'CA', name: "Canada" },
  { code: 'CV', name: "Cape Verde" },
  { code: 'BQ', name: "Caribbean Netherlands" },
  { code: 'KY', name: "Cayman Islands" },
  { code: 'CF', name: "Central African Republic" },
  { code: 'TD', name: "Chad" },
  { code: 'CL', name: "Chile" },
  { code: 'CN', name: "China" },
  { code: 'CX', name: "Christmas Island" },
  { code: 'CC', name: "Cocos (Keeling) Islands" },
  { code: 'CO', name: "Colombia" },
  { code: 'KM', name: "Comoros" },
  { code: 'CG', name: "Congo - Brazzaville" },
  { code: 'CD', name: "Congo - Kinshasa" },
  { code: 'CK', name: "Cook Islands" },
  { code: 'CR', name: "Costa Rica" },
  { code: 'CI', name: "Côte d’Ivoire" },
  { code: 'HR', name: "Croatia" },
  { code: 'CU', name: "Cuba" },
  { code: 'CW', name: "Curaçao" },
  { code: 'CY', name: "Cyprus" },
  { code: 'CZ', name: "Czechia" },
  { code: 'DK', name: "Denmark" },
  { code: 'DJ', name: "Djibouti" },
  { code: 'DM', name: "Dominica" },
  { code: 'DO', name: "Dominican Republic" },
  { code: 'EC', name: "Ecuador" },
  { code: 'EG', name: "Egypt" },
  { code: 'SV', name: "El Salvador" },
  { code: 'GQ', name: "Equatorial Guinea" },
  { code: 'ER', name: "Eritrea" },
  { code: 'EE', name: "Estonia" },
  { code: 'SZ', name: "Eswatini" },
  { code: 'ET', name: "Ethiopia" },
  { code: 'FK', name: "Falkland Islands" },
  { code: 'FO', name: "Faroe Islands" },
  { code: 'FJ', name: "Fiji" },
  { code: 'FI', name: "Finland" },
  { code: 'FR', name: "France" },
  { code: 'GF', name: "French Guiana" },
  { code: 'PF', name: "French Polynesia" },
  { code: 'GA', name: "Gabon" },
  { code: 'GM', name: "Gambia" },
  { code: 'GE', name: "Georgia" },
  { code: 'DE', name: "Germany" },
  { code: 'GH', name: "Ghana" },
  { code: 'GI', name: "Gibraltar" },
  { code: 'GR', name: "Greece" },
  { code: 'GL', name: "Greenland" },
  { code: 'GD', name: "Grenada" },
  { code: 'GP', name: "Guadeloupe" },
  { code: 'GU', name: "Guam" },
  { code: 'GT', name: "Guatemala" },
  { code: 'GG', name: "Guernsey" },
  { code: 'GN', name: "Guinea" },
  { code: 'GW', name: "Guinea-Bissau" },
  { code: 'GY', name: "Guyana" },
  { code: 'HT', name: "Haiti" },
  { code: 'HN', name: "Honduras" },
  { code: 'HK', name: "Hong Kong SAR China" },
  { code: 'HU', name: "Hungary" },
  { code: 'IS', name: "Iceland" },
  { code: 'IN', name: "India" },
  { code: 'ID', name: "Indonesia" },
  { code: 'IR', name: "Iran" },
  { code: 'IQ', name: "Iraq" },
  { code: 'IE', name: "Ireland" },
  { code: 'IM', name: "Isle of Man" },
  { code: 'IL', name: "Israel" },
  { code: 'IT', name: "Italy" },
  { code: 'JM', name: "Jamaica" },
  { code: 'JP', name: "Japan" },
  { code: 'JE', name: "Jersey" },
  { code: 'JO', name: "Jordan" },
  { code: 'KZ', name: "Kazakhstan" },
  { code: 'KE', name: "Kenya" },
  { code: 'KI', name: "Kiribati" },
  { code: 'KW', name: "Kuwait" },
  { code: 'KG', name: "Kyrgyzstan" },
  { code: 'LA', name: "Laos" },
  { code: 'LV', name: "Latvia" },
  { code: 'LB', name: "Lebanon" },
  { code: 'LS', name: "Lesotho" },
  { code: 'LR', name: "Liberia" },
  { code: 'LY', name: "Libya" },
  { code: 'LI', name: "Liechtenstein" },
  { code: 'LT', name: "Lithuania" },
  { code: 'LU', name: "Luxembourg" },
  { code: 'MO', name: "Macao SAR China" },
  { code: 'MG', name: "Madagascar" },
  { code: 'MW', name: "Malawi" },
  { code: 'MY', name: "Malaysia" },
  { code: 'MV', name: "Maldives" },
  { code: 'ML', name: "Mali" },
  { code: 'MT', name: "Malta" },
  { code: 'MH', name: "Marshall Islands" },
  { code: 'MQ', name: "Martinique" },
  { code: 'MR', name: "Mauritania" },
  { code: 'MU', name: "Mauritius" },
  { code: 'YT', name: "Mayotte" },
  { code: 'MX', name: "Mexico" },
  { code: 'FM', name: "Micronesia" },
  { code: 'MD', name: "Moldova" },
  { code: 'MC', name: "Monaco" },
  { code: 'MN', name: "Mongolia" },
  { code: 'ME', name: "Montenegro" },
  { code: 'MS', name: "Montserrat" },
  { code: 'MA', name: "Morocco" },
  { code: 'MZ', name: "Mozambique" },
  { code: 'MM', name: "Myanmar (Burma)" },
  { code: 'NA', name: "Namibia" },
  { code: 'NR', name: "Nauru" },
  { code: 'NP', name: "Nepal" },
  { code: 'NL', name: "Netherlands" },
  { code: 'NC', name: "New Caledonia" },
  { code: 'NZ', name: "New Zealand" },
  { code: 'NI', name: "Nicaragua" },
  { code: 'NE', name: "Niger" },
  { code: 'NG', name: "Nigeria" },
  { code: 'NU', name: "Niue" },
  { code: 'NF', name: "Norfolk Island" },
  { code: 'KP', name: "North Korea" },
  { code: 'MK', name: "North Macedonia" },
  { code: 'MP', name: "Northern Mariana Islands" },
  { code: 'NO', name: "Norway" },
  { code: 'OM', name: "Oman" },
  { code: 'PK', name: "Pakistan" },
  { code: 'PW', name: "Palau" },
  { code: 'PS', name: "Palestinian Territories" },
  { code: 'PA', name: "Panama" },
  { code: 'PG', name: "Papua New Guinea" },
  { code: 'PY', name: "Paraguay" },
  { code: 'PE', name: "Peru" },
  { code: 'PH', name: "Philippines" },
  { code: 'PN', name: "Pitcairn Islands" },
  { code: 'PL', name: "Poland" },
  { code: 'PT', name: "Portugal" },
  { code: 'PR', name: "Puerto Rico" },
  { code: 'QA', name: "Qatar" },
  { code: 'RE', name: "Réunion" },
  { code: 'RO', name: "Romania" },
  { code: 'RU', name: "Russia" },
  { code: 'RW', name: "Rwanda" },
  { code: 'WS', name: "Samoa" },
  { code: 'SM', name: "San Marino" },
  { code: 'ST', name: "São Tomé & Príncipe" },
  { code: 'SA', name: "Saudi Arabia" },
  { code: 'SN', name: "Senegal" },
  { code: 'RS', name: "Serbia" },
  { code: 'SC', name: "Seychelles" },
  { code: 'SL', name: "Sierra Leone" },
  { code: 'SG', name: "Singapore" },
  { code: 'SX', name: "Sint Maarten" },
  { code: 'SK', name: "Slovakia" },
  { code: 'SI', name: "Slovenia" },
  { code: 'SB', name: "Solomon Islands" },
  { code: 'SO', name: "Somalia" },
  { code: 'ZA', name: "South Africa" },
  { code: 'KR', name: "South Korea" },
  { code: 'SS', name: "South Sudan" },
  { code: 'ES', name: "Spain" },
  { code: 'LK', name: "Sri Lanka" },
  { code: 'BL', name: "St. Barthélemy" },
  { code: 'SH', name: "St. Helena" },
  { code: 'KN', name: "St. Kitts & Nevis" },
  { code: 'LC', name: "St. Lucia" },
  { code: 'MF', name: "St. Martin" },
  { code: 'PM', name: "St. Pierre & Miquelon" },
  { code: 'VC', name: "St. Vincent & Grenadines" },
  { code: 'SD', name: "Sudan" },
  { code: 'SR', name: "Suriname" },
  { code: 'SJ', name: "Svalbard & Jan Mayen" },
  { code: 'SE', name: "Sweden" },
  { code: 'CH', name: "Switzerland" },
  { code: 'SY', name: "Syria" },
  { code: 'TW', name: "Taiwan" },
  { code: 'TJ', name: "Tajikistan" },
  { code: 'TZ', name: "Tanzania" },
  { code: 'TH', name: "Thailand" },
  { code: 'TL', name: "Timor-Leste" },
  { code: 'TG', name: "Togo" },
  { code: 'TK', name: "Tokelau" },
  { code: 'TO', name: "Tonga" },
  { code: 'TT', name: "Trinidad & Tobago" },
  { code: 'TN', name: "Tunisia" },
  { code: 'TR', name: "Türkiye" },
  { code: 'TM', name: "Turkmenistan" },
  { code: 'TC', name: "Turks & Caicos Islands" },
  { code: 'TV', name: "Tuvalu" },
  { code: 'VI', name: "U.S. Virgin Islands" },
  { code: 'UG', name: "Uganda" },
  { code: 'UA', name: "Ukraine" },
  { code: 'AE', name: "United Arab Emirates" },
  { code: 'GB', name: "United Kingdom" },
  { code: 'US', name: "United States" },
  { code: 'UY', name: "Uruguay" },
  { code: 'UZ', name: "Uzbekistan" },
  { code: 'VU', name: "Vanuatu" },
  { code: 'VA', name: "Vatican City" },
  { code: 'VE', name: "Venezuela" },
  { code: 'VN', name: "Vietnam" },
  { code: 'WF', name: "Wallis & Futuna" },
  { code: 'EH', name: "Western Sahara" },
  { code: 'YE', name: "Yemen" },
  { code: 'ZM', name: "Zambia" },
  { code: 'ZW', name: "Zimbabwe" },
];

const COUNTRIES_RO: CountryOption[] = [
  { code: 'AF', name: "Afganistan" },
  { code: 'ZA', name: "Africa de Sud" },
  { code: 'AL', name: "Albania" },
  { code: 'DZ', name: "Algeria" },
  { code: 'AD', name: "Andorra" },
  { code: 'AO', name: "Angola" },
  { code: 'AI', name: "Anguilla" },
  { code: 'AG', name: "Antigua și Barbuda" },
  { code: 'SA', name: "Arabia Saudită" },
  { code: 'AR', name: "Argentina" },
  { code: 'AM', name: "Armenia" },
  { code: 'AW', name: "Aruba" },
  { code: 'AU', name: "Australia" },
  { code: 'AT', name: "Austria" },
  { code: 'AZ', name: "Azerbaidjan" },
  { code: 'BS', name: "Bahamas" },
  { code: 'BH', name: "Bahrain" },
  { code: 'BD', name: "Bangladesh" },
  { code: 'BB', name: "Barbados" },
  { code: 'BY', name: "Belarus" },
  { code: 'BE', name: "Belgia" },
  { code: 'BZ', name: "Belize" },
  { code: 'BJ', name: "Benin" },
  { code: 'BM', name: "Bermuda" },
  { code: 'BT', name: "Bhutan" },
  { code: 'BO', name: "Bolivia" },
  { code: 'BA', name: "Bosnia și Herțegovina" },
  { code: 'BW', name: "Botswana" },
  { code: 'BR', name: "Brazilia" },
  { code: 'BN', name: "Brunei" },
  { code: 'BG', name: "Bulgaria" },
  { code: 'BF', name: "Burkina Faso" },
  { code: 'BI', name: "Burundi" },
  { code: 'KH', name: "Cambodgia" },
  { code: 'CM', name: "Camerun" },
  { code: 'CA', name: "Canada" },
  { code: 'CV', name: "Capul Verde" },
  { code: 'CZ', name: "Cehia" },
  { code: 'CL', name: "Chile" },
  { code: 'CN', name: "China" },
  { code: 'TD', name: "Ciad" },
  { code: 'CY', name: "Cipru" },
  { code: 'CO', name: "Columbia" },
  { code: 'KM', name: "Comore" },
  { code: 'CG', name: "Congo - Brazzaville" },
  { code: 'CD', name: "Congo - Kinshasa" },
  { code: 'KP', name: "Coreea de Nord" },
  { code: 'KR', name: "Coreea de Sud" },
  { code: 'CR', name: "Costa Rica" },
  { code: 'CI', name: "Côte d’Ivoire" },
  { code: 'HR', name: "Croația" },
  { code: 'CU', name: "Cuba" },
  { code: 'CW', name: "Curaçao" },
  { code: 'DK', name: "Danemarca" },
  { code: 'DJ', name: "Djibouti" },
  { code: 'DM', name: "Dominica" },
  { code: 'EC', name: "Ecuador" },
  { code: 'EG', name: "Egipt" },
  { code: 'SV', name: "El Salvador" },
  { code: 'CH', name: "Elveția" },
  { code: 'AE', name: "Emiratele Arabe Unite" },
  { code: 'ER', name: "Eritreea" },
  { code: 'EE', name: "Estonia" },
  { code: 'SZ', name: "Eswatini" },
  { code: 'ET', name: "Etiopia" },
  { code: 'FJ', name: "Fiji" },
  { code: 'PH', name: "Filipine" },
  { code: 'FI', name: "Finlanda" },
  { code: 'FR', name: "Franța" },
  { code: 'GA', name: "Gabon" },
  { code: 'GM', name: "Gambia" },
  { code: 'GE', name: "Georgia" },
  { code: 'DE', name: "Germania" },
  { code: 'GH', name: "Ghana" },
  { code: 'GI', name: "Gibraltar" },
  { code: 'GR', name: "Grecia" },
  { code: 'GD', name: "Grenada" },
  { code: 'GL', name: "Groenlanda" },
  { code: 'GP', name: "Guadelupa" },
  { code: 'GU', name: "Guam" },
  { code: 'GT', name: "Guatemala" },
  { code: 'GG', name: "Guernsey" },
  { code: 'GN', name: "Guineea" },
  { code: 'GQ', name: "Guineea Ecuatorială" },
  { code: 'GW', name: "Guineea-Bissau" },
  { code: 'GY', name: "Guyana" },
  { code: 'GF', name: "Guyana Franceză" },
  { code: 'HT', name: "Haiti" },
  { code: 'HN', name: "Honduras" },
  { code: 'IN', name: "India" },
  { code: 'ID', name: "Indonezia" },
  { code: 'CX', name: "Insula Christmas" },
  { code: 'IM', name: "Insula Man" },
  { code: 'NF', name: "Insula Norfolk" },
  { code: 'AX', name: "Insulele Åland" },
  { code: 'BQ', name: "Insulele Caraibe Olandeze" },
  { code: 'KY', name: "Insulele Cayman" },
  { code: 'CC', name: "Insulele Cocos (Keeling)" },
  { code: 'CK', name: "Insulele Cook" },
  { code: 'FK', name: "Insulele Falkland" },
  { code: 'FO', name: "Insulele Feroe" },
  { code: 'MP', name: "Insulele Mariane de Nord" },
  { code: 'MH', name: "Insulele Marshall" },
  { code: 'PN', name: "Insulele Pitcairn" },
  { code: 'SB', name: "Insulele Solomon" },
  { code: 'TC', name: "Insulele Turks și Caicos" },
  { code: 'VI', name: "Insulele Virgine Americane" },
  { code: 'VG', name: "Insulele Virgine Britanice" },
  { code: 'JO', name: "Iordania" },
  { code: 'IQ', name: "Irak" },
  { code: 'IR', name: "Iran" },
  { code: 'IE', name: "Irlanda" },
  { code: 'IS', name: "Islanda" },
  { code: 'IL', name: "Israel" },
  { code: 'IT', name: "Italia" },
  { code: 'JM', name: "Jamaica" },
  { code: 'JP', name: "Japonia" },
  { code: 'JE', name: "Jersey" },
  { code: 'KZ', name: "Kazahstan" },
  { code: 'KG', name: "Kârgâzstan" },
  { code: 'KE', name: "Kenya" },
  { code: 'KI', name: "Kiribati" },
  { code: 'KW', name: "Kuweit" },
  { code: 'LA', name: "Laos" },
  { code: 'LS', name: "Lesotho" },
  { code: 'LV', name: "Letonia" },
  { code: 'LB', name: "Liban" },
  { code: 'LR', name: "Liberia" },
  { code: 'LY', name: "Libia" },
  { code: 'LI', name: "Liechtenstein" },
  { code: 'LT', name: "Lituania" },
  { code: 'LU', name: "Luxemburg" },
  { code: 'MK', name: "Macedonia de Nord" },
  { code: 'MG', name: "Madagascar" },
  { code: 'MW', name: "Malawi" },
  { code: 'MY', name: "Malaysia" },
  { code: 'MV', name: "Maldive" },
  { code: 'ML', name: "Mali" },
  { code: 'MT', name: "Malta" },
  { code: 'MA', name: "Maroc" },
  { code: 'MQ', name: "Martinica" },
  { code: 'MR', name: "Mauritania" },
  { code: 'MU', name: "Mauritius" },
  { code: 'YT', name: "Mayotte" },
  { code: 'MX', name: "Mexic" },
  { code: 'FM', name: "Micronezia" },
  { code: 'MC', name: "Monaco" },
  { code: 'MN', name: "Mongolia" },
  { code: 'MS', name: "Montserrat" },
  { code: 'MZ', name: "Mozambic" },
  { code: 'ME', name: "Muntenegru" },
  { code: 'MM', name: "Myanmar (Birmania)" },
  { code: 'NA', name: "Namibia" },
  { code: 'NR', name: "Nauru" },
  { code: 'NP', name: "Nepal" },
  { code: 'NI', name: "Nicaragua" },
  { code: 'NE', name: "Niger" },
  { code: 'NG', name: "Nigeria" },
  { code: 'NU', name: "Niue" },
  { code: 'NO', name: "Norvegia" },
  { code: 'NC', name: "Noua Caledonie" },
  { code: 'NZ', name: "Noua Zeelandă" },
  { code: 'OM', name: "Oman" },
  { code: 'PK', name: "Pakistan" },
  { code: 'PW', name: "Palau" },
  { code: 'PA', name: "Panama" },
  { code: 'PG', name: "Papua-Noua Guinee" },
  { code: 'PY', name: "Paraguay" },
  { code: 'PE', name: "Peru" },
  { code: 'PF', name: "Polinezia Franceză" },
  { code: 'PL', name: "Polonia" },
  { code: 'PT', name: "Portugalia" },
  { code: 'PR', name: "Puerto Rico" },
  { code: 'QA', name: "Qatar" },
  { code: 'HK', name: "R.A.S. Hong Kong, China" },
  { code: 'MO', name: "R.A.S. Macao, China" },
  { code: 'GB', name: "Regatul Unit" },
  { code: 'CF', name: "Republica Centrafricană" },
  { code: 'DO', name: "Republica Dominicană" },
  { code: 'MD', name: "Republica Moldova" },
  { code: 'RE', name: "Réunion" },
  { code: 'RO', name: "România" },
  { code: 'RU', name: "Rusia" },
  { code: 'RW', name: "Rwanda" },
  { code: 'EH', name: "Sahara Occidentală" },
  { code: 'KN', name: "Saint Kitts și Nevis" },
  { code: 'VC', name: "Saint Vincent și Grenadinele" },
  { code: 'BL', name: "Saint-Barthélemy" },
  { code: 'PM', name: "Saint-Pierre și Miquelon" },
  { code: 'WS', name: "Samoa" },
  { code: 'AS', name: "Samoa Americană" },
  { code: 'SM', name: "San Marino" },
  { code: 'ST', name: "São Tomé și Príncipe" },
  { code: 'SN', name: "Senegal" },
  { code: 'RS', name: "Serbia" },
  { code: 'SC', name: "Seychelles" },
  { code: 'SH', name: "Sfânta Elena" },
  { code: 'LC', name: "Sfânta Lucia" },
  { code: 'MF', name: "Sfântul Martin" },
  { code: 'SL', name: "Sierra Leone" },
  { code: 'SG', name: "Singapore" },
  { code: 'SX', name: "Sint-Maarten" },
  { code: 'SY', name: "Siria" },
  { code: 'SK', name: "Slovacia" },
  { code: 'SI', name: "Slovenia" },
  { code: 'SO', name: "Somalia" },
  { code: 'ES', name: "Spania" },
  { code: 'LK', name: "Sri Lanka" },
  { code: 'US', name: "Statele Unite ale Americii" },
  { code: 'VA', name: "Statul Cetății Vaticanului" },
  { code: 'SD', name: "Sudan" },
  { code: 'SS', name: "Sudanul de Sud" },
  { code: 'SE', name: "Suedia" },
  { code: 'SR', name: "Suriname" },
  { code: 'SJ', name: "Svalbard și Jan Mayen" },
  { code: 'TJ', name: "Tadjikistan" },
  { code: 'TW', name: "Taiwan" },
  { code: 'TZ', name: "Tanzania" },
  { code: 'PS', name: "Teritoriile Palestiniene" },
  { code: 'IO', name: "Teritoriul Britanic din Oceanul Indian" },
  { code: 'TH', name: "Thailanda" },
  { code: 'TL', name: "Timor-Leste" },
  { code: 'TG', name: "Togo" },
  { code: 'TK', name: "Tokelau" },
  { code: 'TO', name: "Tonga" },
  { code: 'TT', name: "Trinidad și Tobago" },
  { code: 'TN', name: "Tunisia" },
  { code: 'TR', name: "Turcia" },
  { code: 'TM', name: "Turkmenistan" },
  { code: 'TV', name: "Tuvalu" },
  { code: 'NL', name: "Țările de Jos" },
  { code: 'UA', name: "Ucraina" },
  { code: 'UG', name: "Uganda" },
  { code: 'HU', name: "Ungaria" },
  { code: 'UY', name: "Uruguay" },
  { code: 'UZ', name: "Uzbekistan" },
  { code: 'VU', name: "Vanuatu" },
  { code: 'VE', name: "Venezuela" },
  { code: 'VN', name: "Vietnam" },
  { code: 'WF', name: "Wallis și Futuna" },
  { code: 'YE', name: "Yemen" },
  { code: 'ZM', name: "Zambia" },
  { code: 'ZW', name: "Zimbabwe" },
];

const COUNTRIES_HU: CountryOption[] = [
  { code: 'AF', name: "Afganisztán" },
  { code: 'AX', name: "Åland-szigetek" },
  { code: 'AL', name: "Albánia" },
  { code: 'DZ', name: "Algéria" },
  { code: 'AS', name: "Amerikai Szamoa" },
  { code: 'VI', name: "Amerikai Virgin-szigetek" },
  { code: 'AD', name: "Andorra" },
  { code: 'AO', name: "Angola" },
  { code: 'AI', name: "Anguilla" },
  { code: 'AG', name: "Antigua és Barbuda" },
  { code: 'AR', name: "Argentína" },
  { code: 'AW', name: "Aruba" },
  { code: 'AU', name: "Ausztrália" },
  { code: 'AT', name: "Ausztria" },
  { code: 'AZ', name: "Azerbajdzsán" },
  { code: 'BS', name: "Bahama-szigetek" },
  { code: 'BH', name: "Bahrein" },
  { code: 'BD', name: "Banglades" },
  { code: 'BB', name: "Barbados" },
  { code: 'BY', name: "Belarusz" },
  { code: 'BE', name: "Belgium" },
  { code: 'BZ', name: "Belize" },
  { code: 'BJ', name: "Benin" },
  { code: 'BM', name: "Bermuda" },
  { code: 'BT', name: "Bhután" },
  { code: 'GW', name: "Bissau-Guinea" },
  { code: 'BO', name: "Bolívia" },
  { code: 'BA', name: "Bosznia-Hercegovina" },
  { code: 'BW', name: "Botswana" },
  { code: 'BR', name: "Brazília" },
  { code: 'IO', name: "Brit Indiai-óceáni Terület" },
  { code: 'VG', name: "Brit Virgin-szigetek" },
  { code: 'BN', name: "Brunei" },
  { code: 'BG', name: "Bulgária" },
  { code: 'BF', name: "Burkina Faso" },
  { code: 'BI', name: "Burundi" },
  { code: 'CL', name: "Chile" },
  { code: 'CY', name: "Ciprus" },
  { code: 'KM', name: "Comore-szigetek" },
  { code: 'CK', name: "Cook-szigetek" },
  { code: 'CR', name: "Costa Rica" },
  { code: 'CW', name: "Curaçao" },
  { code: 'TD', name: "Csád" },
  { code: 'CZ', name: "Csehország" },
  { code: 'DK', name: "Dánia" },
  { code: 'ZA', name: "Dél-afrikai Köztársaság" },
  { code: 'KR', name: "Dél-Korea" },
  { code: 'SS', name: "Dél-Szudán" },
  { code: 'DM', name: "Dominika" },
  { code: 'DO', name: "Dominikai Köztársaság" },
  { code: 'DJ', name: "Dzsibuti" },
  { code: 'EC', name: "Ecuador" },
  { code: 'GQ', name: "Egyenlítői-Guinea" },
  { code: 'US', name: "Egyesült Államok" },
  { code: 'AE', name: "Egyesült Arab Emírségek" },
  { code: 'GB', name: "Egyesült Királyság" },
  { code: 'EG', name: "Egyiptom" },
  { code: 'CI', name: "Elefántcsontpart" },
  { code: 'ER', name: "Eritrea" },
  { code: 'KP', name: "Észak-Korea" },
  { code: 'MK', name: "Észak-Macedónia" },
  { code: 'MP', name: "Északi Mariana-szigetek" },
  { code: 'EE', name: "Észtország" },
  { code: 'ET', name: "Etiópia" },
  { code: 'FK', name: "Falkland-szigetek" },
  { code: 'FO', name: "Feröer szigetek" },
  { code: 'FJ', name: "Fidzsi" },
  { code: 'FI', name: "Finnország" },
  { code: 'GF', name: "Francia Guyana" },
  { code: 'PF', name: "Francia Polinézia" },
  { code: 'FR', name: "Franciaország" },
  { code: 'PH', name: "Fülöp-szigetek" },
  { code: 'GA', name: "Gabon" },
  { code: 'GM', name: "Gambia" },
  { code: 'GH', name: "Ghána" },
  { code: 'GI', name: "Gibraltár" },
  { code: 'GR', name: "Görögország" },
  { code: 'GD', name: "Grenada" },
  { code: 'GL', name: "Grönland" },
  { code: 'GE', name: "Grúzia" },
  { code: 'GP', name: "Guadeloupe" },
  { code: 'GU', name: "Guam" },
  { code: 'GT', name: "Guatemala" },
  { code: 'GG', name: "Guernsey" },
  { code: 'GN', name: "Guinea" },
  { code: 'GY', name: "Guyana" },
  { code: 'HT', name: "Haiti" },
  { code: 'BQ', name: "Holland Karib-térség" },
  { code: 'NL', name: "Hollandia" },
  { code: 'HN', name: "Honduras" },
  { code: 'HK', name: "Hongkong KKT" },
  { code: 'HR', name: "Horvátország" },
  { code: 'IN', name: "India" },
  { code: 'ID', name: "Indonézia" },
  { code: 'IQ', name: "Irak" },
  { code: 'IR', name: "Irán" },
  { code: 'IE', name: "Írország" },
  { code: 'IS', name: "Izland" },
  { code: 'IL', name: "Izrael" },
  { code: 'JM', name: "Jamaica" },
  { code: 'JP', name: "Japán" },
  { code: 'YE', name: "Jemen" },
  { code: 'JE', name: "Jersey" },
  { code: 'JO', name: "Jordánia" },
  { code: 'KY', name: "Kajmán-szigetek" },
  { code: 'KH', name: "Kambodzsa" },
  { code: 'CM', name: "Kamerun" },
  { code: 'CA', name: "Kanada" },
  { code: 'CX', name: "Karácsony-sziget" },
  { code: 'QA', name: "Katar" },
  { code: 'KZ', name: "Kazahsztán" },
  { code: 'TL', name: "Kelet-Timor" },
  { code: 'KE', name: "Kenya" },
  { code: 'CN', name: "Kína" },
  { code: 'KG', name: "Kirgizisztán" },
  { code: 'KI', name: "Kiribati" },
  { code: 'CC', name: "Kókusz (Keeling)-szigetek" },
  { code: 'CO', name: "Kolumbia" },
  { code: 'CG', name: "Kongó – Brazzaville" },
  { code: 'CD', name: "Kongó – Kinshasa" },
  { code: 'CF', name: "Közép-afrikai Köztársaság" },
  { code: 'CU', name: "Kuba" },
  { code: 'KW', name: "Kuvait" },
  { code: 'LA', name: "Laosz" },
  { code: 'PL', name: "Lengyelország" },
  { code: 'LS', name: "Lesotho" },
  { code: 'LV', name: "Lettország" },
  { code: 'LB', name: "Libanon" },
  { code: 'LR', name: "Libéria" },
  { code: 'LY', name: "Líbia" },
  { code: 'LI', name: "Liechtenstein" },
  { code: 'LT', name: "Litvánia" },
  { code: 'LU', name: "Luxemburg" },
  { code: 'MG', name: "Madagaszkár" },
  { code: 'HU', name: "Magyarország" },
  { code: 'MO', name: "Makaó KKT" },
  { code: 'MY', name: "Malajzia" },
  { code: 'MW', name: "Malawi" },
  { code: 'MV', name: "Maldív-szigetek" },
  { code: 'ML', name: "Mali" },
  { code: 'MT', name: "Málta" },
  { code: 'IM', name: "Man-sziget" },
  { code: 'MA', name: "Marokkó" },
  { code: 'MH', name: "Marshall-szigetek" },
  { code: 'MQ', name: "Martinique" },
  { code: 'MR', name: "Mauritánia" },
  { code: 'MU', name: "Mauritius" },
  { code: 'YT', name: "Mayotte" },
  { code: 'MX', name: "Mexikó" },
  { code: 'MM', name: "Mianmar" },
  { code: 'FM', name: "Mikronézia" },
  { code: 'MD', name: "Moldova" },
  { code: 'MC', name: "Monaco" },
  { code: 'MN', name: "Mongólia" },
  { code: 'ME', name: "Montenegró" },
  { code: 'MS', name: "Montserrat" },
  { code: 'MZ', name: "Mozambik" },
  { code: 'NA', name: "Namíbia" },
  { code: 'NR', name: "Nauru" },
  { code: 'DE', name: "Németország" },
  { code: 'NP', name: "Nepál" },
  { code: 'NI', name: "Nicaragua" },
  { code: 'NE', name: "Niger" },
  { code: 'NG', name: "Nigéria" },
  { code: 'NU', name: "Niue" },
  { code: 'NF', name: "Norfolk-sziget" },
  { code: 'NO', name: "Norvégia" },
  { code: 'EH', name: "Nyugat-Szahara" },
  { code: 'IT', name: "Olaszország" },
  { code: 'OM', name: "Omán" },
  { code: 'RU', name: "Oroszország" },
  { code: 'AM', name: "Örményország" },
  { code: 'PK', name: "Pakisztán" },
  { code: 'PW', name: "Palau" },
  { code: 'PS', name: "Palesztin Autonómia" },
  { code: 'PA', name: "Panama" },
  { code: 'PG', name: "Pápua Új-Guinea" },
  { code: 'PY', name: "Paraguay" },
  { code: 'PE', name: "Peru" },
  { code: 'PN', name: "Pitcairn-szigetek" },
  { code: 'PT', name: "Portugália" },
  { code: 'PR', name: "Puerto Rico" },
  { code: 'RE', name: "Réunion" },
  { code: 'RO', name: "Románia" },
  { code: 'RW', name: "Ruanda" },
  { code: 'KN', name: "Saint Kitts és Nevis" },
  { code: 'LC', name: "Saint Lucia" },
  { code: 'MF', name: "Saint Martin" },
  { code: 'VC', name: "Saint Vincent és a Grenadine-szigetek" },
  { code: 'BL', name: "Saint-Barthélemy" },
  { code: 'PM', name: "Saint-Pierre és Miquelon" },
  { code: 'SB', name: "Salamon-szigetek" },
  { code: 'SV', name: "Salvador" },
  { code: 'SM', name: "San Marino" },
  { code: 'ST', name: "São Tomé és Príncipe" },
  { code: 'SC', name: "Seychelle-szigetek" },
  { code: 'SL', name: "Sierra Leone" },
  { code: 'SX', name: "Sint Maarten" },
  { code: 'ES', name: "Spanyolország" },
  { code: 'LK', name: "Srí Lanka" },
  { code: 'SR', name: "Suriname" },
  { code: 'CH', name: "Svájc" },
  { code: 'SJ', name: "Svalbard és Jan Mayen" },
  { code: 'SE', name: "Svédország" },
  { code: 'WS', name: "Szamoa" },
  { code: 'SA', name: "Szaúd-Arábia" },
  { code: 'SN', name: "Szenegál" },
  { code: 'SH', name: "Szent Ilona" },
  { code: 'RS', name: "Szerbia" },
  { code: 'SG', name: "Szingapúr" },
  { code: 'SY', name: "Szíria" },
  { code: 'SK', name: "Szlovákia" },
  { code: 'SI', name: "Szlovénia" },
  { code: 'SO', name: "Szomália" },
  { code: 'SD', name: "Szudán" },
  { code: 'SZ', name: "Szváziföld" },
  { code: 'TJ', name: "Tádzsikisztán" },
  { code: 'TW', name: "Tajvan" },
  { code: 'TZ', name: "Tanzánia" },
  { code: 'TH', name: "Thaiföld" },
  { code: 'TG', name: "Togo" },
  { code: 'TK', name: "Tokelau" },
  { code: 'TO', name: "Tonga" },
  { code: 'TR', name: "Törökország" },
  { code: 'TT', name: "Trinidad és Tobago" },
  { code: 'TN', name: "Tunézia" },
  { code: 'TC', name: "Turks- és Caicos-szigetek" },
  { code: 'TV', name: "Tuvalu" },
  { code: 'TM', name: "Türkmenisztán" },
  { code: 'UG', name: "Uganda" },
  { code: 'NC', name: "Új-Kaledónia" },
  { code: 'NZ', name: "Új-Zéland" },
  { code: 'UA', name: "Ukrajna" },
  { code: 'UY', name: "Uruguay" },
  { code: 'UZ', name: "Üzbegisztán" },
  { code: 'VU', name: "Vanuatu" },
  { code: 'VA', name: "Vatikán" },
  { code: 'VE', name: "Venezuela" },
  { code: 'VN', name: "Vietnám" },
  { code: 'WF', name: "Wallis és Futuna" },
  { code: 'ZM', name: "Zambia" },
  { code: 'ZW', name: "Zimbabwe" },
  { code: 'CV', name: "Zöld-foki Köztársaság" },
];

const BY_LOCALE: Record<string, CountryOption[]> = {
  en: COUNTRIES_EN,
  ro: COUNTRIES_RO,
  hu: COUNTRIES_HU,
};

/** All countries, alphabetical in the given locale. Falls back to English. */
export function getCountryOptions(locale: string): CountryOption[] {
  return BY_LOCALE[locale] || BY_LOCALE.en;
}

/**
 * The pinned shortlist, in `FREQUENT_COUNTRY_CODES` order (not alphabetical —
 * the point of the group is that the likely answers come first).
 */
export function getFrequentCountryOptions(locale: string): CountryOption[] {
  const all = getCountryOptions(locale);
  return FREQUENT_COUNTRY_CODES.map((code) => all.find((c) => c.code === code)).filter(
    (c): c is CountryOption => Boolean(c)
  );
}

/**
 * Server-side allowlist for the `guestCountry` / `buyerCountry` field. The API
 * routes forward this value straight to PYN as `guestCountryCode` and persist it
 * on the booking, so an unrecognised string must never get that far — the UI can
 * only ever produce a code from this list.
 */
export function isValidCountryCode(code: string): boolean {
  return COUNTRIES_EN.some((c) => c.code === code);
}

/** Display name for a stored code, or the raw code when unknown. */
export function getCountryName(code: string, locale: string): string {
  return getCountryOptions(locale).find((c) => c.code === code)?.name || code;
}
